/**
 * HOW FAR PAST THE LAST BAR THE AXIS MAY REACH — the browser's half of the rule.
 *
 * <pre>
 *   ADMIN     last bar + 1 year
 *   REGULAR   the end of the last bar's own calendar quarter
 *   CAPPED    the last bar, and no further        (trial, signed out, unknown)
 * </pre>
 *
 * <h2>⚠ THIS IS THE AXIS, NOT THE ENFORCEMENT</h2>
 *
 * The authority is `security/FutureProjectionCeiling` on the server, which clamps every forward
 * request before a study reads its range — because a POST carrying a date range can be sent by
 * anything, and a restriction that lives only here is a restriction a network panel removes. What
 * this file does is make the CHART agree with what the server will answer: an axis reaching three
 * months past a ceiling would draw three months of empty canvas and read as a study that stopped
 * working.
 *
 * <p>Two implementations of one rule is unavoidable across a wire. `futureProjection.crosslang.test.js`
 * reads the Java off disk and sweeps the quarter arithmetic against it, because Java cannot hand a
 * string to JavaScript and the two drifting apart is the only way either can be wrong here.
 *
 * <h2>⚠ THE TIER COMES FROM THE SERVER, not from a role string parsed here</h2>
 *
 * `/api/chart-window` answers `projectionTier`. Deriving the tier from the role name instead —
 * "ends in -trial", "is Admin" — would be a THIRD place the role-to-tier mapping lives, and the
 * one that would silently disagree the first time a role was added. `TIERS` below is the wire
 * contract and nothing else.
 *
 * <h2>⚠ ISO DAY STRINGS, not Date objects — a stated deviation from the brief</h2>
 *
 * The specified helper returns `new Date(year, 2, 31, 23, 59, 59, 999)`. Three problems, and the
 * first two are the ones that bite:
 *
 * <ul>
 *   <li>`new Date(y, m, d)` is LOCAL while `new Date('2026-10-31')` is UTC midnight, so the
 *       specified helper mixes the two and a reader west of UTC gets a boundary a day out.</li>
 *   <li>`new Date(year, 2, 31)` is right for March and `(year, 5, 30)` for June — four hardcoded
 *       day-of-month literals, of which a transposed pair is wrong by a day in ONE quarter. The
 *       last day comes from the calendar here instead.</li>
 *   <li>A time of 23:59:59.999 has no meaning on a daily axis of date strings, and every date
 *       comparison in this codebase is a lexicographic compare on `yyyy-MM-dd` — which is
 *       chronological, needs no parsing, and cannot be shifted by a timezone.</li>
 * </ul>
 *
 * The inclusive LAST DAY is returned instead. `asDate()` is there for a caller that genuinely
 * needs a Date, and it builds one in UTC.
 */

/** The three tiers, exactly as `/api/chart-window` sends them. ⚠ A WIRE CONTRACT. */
export const TIERS = Object.freeze({
  ADMIN: 'ADMIN',
  REGULAR: 'REGULAR',
  CAPPED: 'CAPPED',
});

/** How far forward an administrator may see. */
export const ADMIN_YEARS_AHEAD = 1;

/** The ISO day at the front of a date or a timestamp, or null. */
export function isoDay(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (s.length < 10) return null;
  const day = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

const pad = (v, w) => String(v).padStart(w, '0');

/** Days in a month, UTC. Day 0 of the NEXT month is the last day of this one. */
const daysInMonth = (year, monthIndex) =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

/**
 * The last day of the quarter {@code day} falls in.
 *
 * <p>Q1 ends 31 March, Q2 30 June, Q3 30 September, Q4 31 December — DERIVED, so none of those
 * four can be mistyped. The month arithmetic is on plain integers and the last day comes from the
 * calendar, which is the same shape the Java takes.
 */
export function endOfQuarter(lastBarDate) {
  const day = isoDay(lastBarDate);
  if (!day) return null;
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));           // 1-12
  // 1-3 -> 3, 4-6 -> 6, 7-9 -> 9, 10-12 -> 12.
  const lastMonth = Math.floor((month - 1) / 3) * 3 + 3;
  return `${pad(year, 4)}-${pad(lastMonth, 2)}-${pad(daysInMonth(year, lastMonth - 1), 2)}`;
}

/**
 * The same day, {@code years} later, CLAMPED into its month.
 *
 * <p>29 February plus a year is 28 February, not 1 March. `Date.prototype.setFullYear` overflows
 * forward exactly as `setMonth` does — the defect `trialSignalCutoff` records in the other
 * direction — and Java's own `LocalDate.plusYears` clamps, so this has to as well or the two
 * sides disagree on one day in four years.
 */
export function yearsAfter(lastBarDate, years = ADMIN_YEARS_AHEAD) {
  const day = isoDay(lastBarDate);
  if (!day) return null;
  const year = Number(day.slice(0, 4)) + Math.floor(years);
  const month = Number(day.slice(5, 7));
  const d = Number(day.slice(8, 10));
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(Math.min(d, daysInMonth(year, month - 1)), 2)}`;
}

/**
 * THE INCLUSIVE CEILING for a tier, as an ISO day.
 *
 * @param {string|number|Date|null} lastBarDate the last bar the market actually traded. ⚠ NOT the
 *        padded axis — `extendBarData` appends 250 synthetic weekdays, so measured from the
 *        chart's final column a quarter-end ceiling would already be a year past real price and
 *        would bound nothing.
 * @param {string|null} tier one of {@link TIERS}. Anything unrecognised is CAPPED, which is the
 *        safe direction: an unknown tier withholds projection rather than granting a year of it.
 * @returns {string|null} the last day that may be drawn, or null when there is no bar to measure
 *          from — null rather than a sentinel, so "no restriction" and "nothing to restrict" stay
 *          distinguishable.
 */
export function getChartFutureCeilingDate(lastBarDate, tier) {
  const day = isoDay(lastBarDate);
  if (!day) return null;
  switch (tier) {
    case TIERS.ADMIN:
      return yearsAfter(day, ADMIN_YEARS_AHEAD);
    case TIERS.REGULAR:
      return endOfQuarter(day);
    default:
      return day;
  }
}

/**
 * ...and the tier for a client that has not been told one.
 *
 * <p>⚠ A FALLBACK, not the rule. `/api/chart-window` sends `projectionTier` and that is what
 * should be used; this exists for the window between mount and the first response, and for a
 * backend deployed before the field existed. It reads the flags `useAuth()` already derives, which
 * is why it cannot be the primary: those come from the PROFILE, and a fallback user whose profile
 * never arrived is neither admin nor trial — so it would resolve to REGULAR and grant a quarter.
 * The server is what refuses; this only decides how much empty canvas to draw meanwhile.
 */
export function fallbackTier({ isAdmin = false, isTrial = false } = {}) {
  if (isAdmin) return TIERS.ADMIN;
  if (isTrial) return TIERS.CAPPED;
  return TIERS.REGULAR;
}

export default getChartFutureCeilingDate;
